const TEST_LOCAL_PATTERNS: RegExp[] = [/^test\+/i, /^seed\+/i, /\+test/i, /\+dev/i, /\+staging/i];

/** Domains allowed for prod `+test` signups and automatic employee discount at Checkout. */
const PROD_PAYMENT_TEST_DOMAINS = new Set(["futurecraft.email", "unilytics.app"]);

export function parseEmailParts(email: string): { local: string; domain: string } | null {
	const normalized = email.trim().toLowerCase();
	const at = normalized.lastIndexOf("@");
	if (at < 0) return null;
	return { local: normalized.slice(0, at), domain: normalized.slice(at + 1) };
}

/** Local part contains `+test` (e.g. alice+test, alice+testqa). */
export function isPlusTestEmail(email: string): boolean {
	const parts = parseEmailParts(email);
	return parts ? /\+test/i.test(parts.local) : false;
}

/** Prod payment-test / employee-discount: `+test` on allowlisted domains. */
export function isProdEmployeeDiscountEmail(email: string): boolean {
	const parts = parseEmailParts(email);
	return parts ? PROD_PAYMENT_TEST_DOMAINS.has(parts.domain) && /\+test/i.test(parts.local) : false;
}

export type TestUserClassification = {
	isTest: boolean;
	/** Only set when isTest — Better Auth hook merges into DB */
	testCreatedBy: string | null;
	testExpiresAt: Date | null;
};

/**
 * Heuristic: treat obvious QA patterns in the local-part as test users.
 * Manual-inferred expiry for non-seed patterns (plan default: 14 days).
 */
export function classifyEmailForNewUser(email: string): TestUserClassification {
	const local = email.split("@")[0] ?? "";
	const isTest = TEST_LOCAL_PATTERNS.some((p) => p.test(local));
	if (!isTest) {
		return { isTest: false, testCreatedBy: null, testExpiresAt: null };
	}

	let testCreatedBy = "manual-inferred";
	const testExpiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);

	if (/^seed\+/i.test(local)) {
		testCreatedBy = "seed";
		return {
			isTest: true,
			testCreatedBy,
			testExpiresAt: null,
		};
	}
	if (/^test\+/i.test(local)) {
		testCreatedBy = "manual-inferred";
	}

	return {
		isTest: true,
		testCreatedBy,
		testExpiresAt,
	};
}

export function isTestEmailPattern(email: string): boolean {
	return classifyEmailForNewUser(email).isTest;
}

/**
 * Affiliate revenue postbacks (e.g. Awesomely) must not fire for these users.
 * Attribution rows in `attribution_events` are still written as usual.
 */
export function shouldSuppressAffiliateRevenuePostback(
	email: string,
	options?: { userMarkedTest?: boolean },
): boolean {
	if (options?.userMarkedTest === true) return true;
	return isTestEmailPattern(email.trim().toLowerCase());
}

/** Block test-pattern signups in production unless prod employee-discount email. */
export function isTestSignupBlockedInProduction(email: string): boolean {
	const c = classifyEmailForNewUser(email);
	return c.isTest && !isProdEmployeeDiscountEmail(email);
}

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

/** Resolves `users` test columns for admin API insert (bypasses Better Auth hooks). */
export function resolveTestUserFieldsForAdminCreate(
	email: string,
	createAsTestUser: boolean,
): {
	isTest: boolean;
	testCreatedAt: Date | null;
	testCreatedBy: string | null;
	testExpiresAt: Date | null;
} {
	const classified = classifyEmailForNewUser(email);
	if (classified.isTest) {
		return {
			isTest: true,
			testCreatedAt: new Date(),
			testCreatedBy: classified.testCreatedBy,
			testExpiresAt: classified.testExpiresAt,
		};
	}
	if (createAsTestUser) {
		return {
			isTest: true,
			testCreatedAt: new Date(),
			testCreatedBy: "admin",
			testExpiresAt: new Date(Date.now() + FOURTEEN_DAYS_MS),
		};
	}
	return {
		isTest: false,
		testCreatedAt: null,
		testCreatedBy: null,
		testExpiresAt: null,
	};
}

/** Team inboxes: deliver real email for these test addresses (adjust domain if needed). */
export function isTeamTestAliasEmail(email: string): boolean {
	const local = email.split("@")[0] ?? "";
	return /^test\+[a-z][a-z0-9-]*$/i.test(local) || /^seed\+/i.test(local);
}
